import React, { useEffect, useState } from 'react';
import { Button, Table, Popup, Label, Modal, Form, Message, Pagination, Segment, Grid, Input } from 'semantic-ui-react';
import { API, showError, showSuccess } from '../helpers';
import { renderQuota } from '../helpers/render';

const ActionStatsTable = () => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [keyword, setKeyword] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [rechargeModalOpen, setRechargeModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [rechargeAmount, setRechargeAmount] = useState('');
  const [updateForm, setUpdateForm] = useState({
    total_actions: '',
    used_actions: '',
    daily_limit: '',
    daily_used: ''
  });

  const loadStats = async (page, kw) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page,
        page_size: pageSize
      });
      if (kw) params.append('keyword', kw);
      const res = await API.get(`/api/admin/actions/stats?${params.toString()}`);
      const data = res.data;
      if (data.stats !== undefined) {
        setStats(data.stats || []);
        setTotal(data.total || 0);
      } else {
        showError('加载统计数据失败');
      }
    } catch (error) {
      showError('加载统计数据失败: ' + error.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadStats(activePage, searchKeyword);
  }, [activePage, searchKeyword]);

  const handleSearch = () => {
    setSearchKeyword(keyword);
    setActivePage(1);
  };

  const handleClearSearch = () => {
    setKeyword('');
    setSearchKeyword('');
    setActivePage(1);
  };

  const onPaginationChange = (e, { activePage }) => {
    setActivePage(activePage);
  };

  const handleRecharge = async () => {
    if (!rechargeAmount || rechargeAmount <= 0) {
      showError('请输入有效的充值数量');
      return;
    }
    try {
      const res = await API.post(`/api/user/${selectedUser.user_id}/actions/recharge`, {
        amount: parseInt(rechargeAmount)
      });
      const { success, message } = res.data;
      if (success) {
        showSuccess('充值成功');
        setRechargeModalOpen(false);
        setRechargeAmount('');
        await loadStats(activePage, searchKeyword);
      } else {
        showError(message || '充值失败');
      }
    } catch (error) {
      showError('充值失败: ' + error.message);
    }
  };

  const handleUpdate = async () => {
    try {
      const payload = {};
      if (updateForm.total_actions !== '') payload.total_actions = parseInt(updateForm.total_actions);
      if (updateForm.used_actions !== '') payload.used_actions = parseInt(updateForm.used_actions);
      if (updateForm.daily_limit !== '') payload.daily_limit = parseInt(updateForm.daily_limit);
      if (updateForm.daily_used !== '') payload.daily_used = parseInt(updateForm.daily_used);

      const res = await API.put(`/api/user/${selectedUser.user_id}/actions`, payload);
      const { success, message } = res.data;
      if (success) {
        showSuccess('更新成功');
        setUpdateModalOpen(false);
        setUpdateForm({ total_actions: '', used_actions: '', daily_limit: '', daily_used: '' });
        await loadStats(activePage, searchKeyword);
      } else {
        showError(message || '更新失败');
      }
    } catch (error) {
      showError('更新失败: ' + error.message);
    }
  };

  const handleResetDaily = async (userId) => {
    try {
      const res = await API.post(`/api/user/${userId}/actions/reset-daily`);
      const { success, message } = res.data;
      if (success) {
        showSuccess('重置成功');
        await loadStats(activePage, searchKeyword);
      } else {
        showError(message || '重置失败');
      }
    } catch (error) {
      showError('重置失败: ' + error.message);
    }
  };

  const openRechargeModal = (user) => {
    setSelectedUser(user);
    setRechargeModalOpen(true);
  };

  const openUpdateModal = (user) => {
    setSelectedUser(user);
    setUpdateForm({
      total_actions: user.total_actions,
      used_actions: user.used_actions,
      daily_limit: user.daily_limit,
      daily_used: user.daily_used
    });
    setUpdateModalOpen(true);
  };

  const renderPlan = (plan) => {
    const planColors = {
      FREE: 'grey', ONE_TIME: 'blue',
      BASIC_MONTHLY: 'green', BASIC_YEARLY: 'teal',
      PLUS_MONTHLY: 'orange', PLUS_YEARLY: 'red'
    };
    const planLabels = {
      FREE: '免费', ONE_TIME: '一次性',
      BASIC_MONTHLY: '基础月付', BASIC_YEARLY: '基础年付',
      PLUS_MONTHLY: '高级月付', PLUS_YEARLY: '高级年付'
    };
    return <Label color={planColors[plan] || 'grey'}>{planLabels[plan] || plan}</Label>;
  };

  return (
    <>
      <Segment>
        <Grid columns={2}>
          <Grid.Column width={8}>
            <Input
              placeholder='搜索用户名或邮箱'
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              action={
                <>
                  <Button primary onClick={handleSearch}>搜索</Button>
                  <Button onClick={handleClearSearch}>清空</Button>
                </>
              }
            />
          </Grid.Column>
        </Grid>
      </Segment>

      <Table basic='very' loading={loading}>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>用户</Table.HeaderCell>
            <Table.HeaderCell>邮箱</Table.HeaderCell>
            <Table.HeaderCell>订阅计划</Table.HeaderCell>
            <Table.HeaderCell>总 Actions</Table.HeaderCell>
            <Table.HeaderCell>已用 Actions</Table.HeaderCell>
            <Table.HeaderCell>剩余 Actions</Table.HeaderCell>
            <Table.HeaderCell>每日限额</Table.HeaderCell>
            <Table.HeaderCell>今日已用</Table.HeaderCell>
            <Table.HeaderCell>今日剩余</Table.HeaderCell>
            <Table.HeaderCell>操作</Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {stats.map((stat) => {
            const remaining = stat.total_actions - stat.used_actions;
            const dailyRemaining = stat.daily_limit > 0 ? stat.daily_limit - stat.daily_used : 0;
            return (
              <Table.Row key={stat.user_id}>
                <Table.Cell>
                  <a href={`/user/edit/${stat.user_id}`} target='_blank' rel='noopener noreferrer'>
                    {stat.username || `#${stat.user_id}`}
                  </a>
                </Table.Cell>
                <Table.Cell>
                  <span
                    style={{ cursor: 'pointer' }}
                    title='点击复制'
                    onClick={() => { navigator.clipboard.writeText(stat.email); }}
                  >{stat.email}</span>
                </Table.Cell>
                <Table.Cell>{renderPlan(stat.plan)}</Table.Cell>
                <Table.Cell>{stat.total_actions}</Table.Cell>
                <Table.Cell>{stat.used_actions}</Table.Cell>
                <Table.Cell>
                  <Label color={remaining > 0 ? 'green' : 'red'}>{remaining}</Label>
                </Table.Cell>
                <Table.Cell>{stat.daily_limit > 0 ? stat.daily_limit : '无限'}</Table.Cell>
                <Table.Cell>{stat.daily_used}</Table.Cell>
                <Table.Cell>
                  {stat.daily_limit > 0 ? (
                    <Label color={dailyRemaining > 0 ? 'green' : 'red'}>{dailyRemaining}</Label>
                  ) : '∞'}
                </Table.Cell>
                <Table.Cell>
                  <Popup trigger={<Button size='small' positive onClick={() => openRechargeModal(stat)}>充值</Button>}
                    content='为用户充值 Actions' position='top center' />
                  <Popup trigger={<Button size='small' onClick={() => openUpdateModal(stat)}>编辑</Button>}
                    content='编辑用户配额' position='top center' />
                  <Popup trigger={<Button size='small' onClick={() => handleResetDaily(stat.user_id)}>重置每日</Button>}
                    content='重置每日配额' position='top center' />
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>

        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan='10'>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>共 {total} 条记录</div>
                <Pagination
                  activePage={activePage}
                  onPageChange={onPaginationChange}
                  totalPages={Math.max(1, Math.ceil(total / pageSize))}
                  siblingRange={1}
                  firstItem={null}
                  lastItem={null}
                />
              </div>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>

      {/* 充值模态框 */}
      <Modal open={rechargeModalOpen} onClose={() => setRechargeModalOpen(false)} size='tiny'>
        <Modal.Header>充值 Actions</Modal.Header>
        <Modal.Content>
          {selectedUser && (
            <>
              <Message>
                <Message.Header>用户信息</Message.Header>
                <p>用户名: {selectedUser.username}</p>
                <p>邮箱: {selectedUser.email}</p>
                <p>当前总 Actions: {selectedUser.total_actions}</p>
                <p>剩余 Actions: {selectedUser.total_actions - selectedUser.used_actions}</p>
              </Message>
              <Form>
                <Form.Input
                  label='充值数量'
                  placeholder='请输入充值的 Actions 数量'
                  type='number'
                  value={rechargeAmount}
                  onChange={(e) => setRechargeAmount(e.target.value)}
                />
              </Form>
            </>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setRechargeModalOpen(false)}>取消</Button>
          <Button positive onClick={handleRecharge}>确认充值</Button>
        </Modal.Actions>
      </Modal>

      {/* 编辑模态框 */}
      <Modal open={updateModalOpen} onClose={() => setUpdateModalOpen(false)} size='small'>
        <Modal.Header>编辑 Action 配额</Modal.Header>
        <Modal.Content>
          {selectedUser && (
            <>
              <Message>
                <Message.Header>用户信息</Message.Header>
                <p>用户名: {selectedUser.username}</p>
                <p>邮箱: {selectedUser.email}</p>
              </Message>
              <Form>
                <Form.Input label='总 Actions' placeholder='留空表示不修改' type='number'
                  value={updateForm.total_actions}
                  onChange={(e) => setUpdateForm({ ...updateForm, total_actions: e.target.value })} />
                <Form.Input label='已用 Actions' placeholder='留空表示不修改' type='number'
                  value={updateForm.used_actions}
                  onChange={(e) => setUpdateForm({ ...updateForm, used_actions: e.target.value })} />
                <Form.Input label='每日限额' placeholder='留空表示不修改' type='number'
                  value={updateForm.daily_limit}
                  onChange={(e) => setUpdateForm({ ...updateForm, daily_limit: e.target.value })} />
                <Form.Input label='今日已用' placeholder='留空表示不修改' type='number'
                  value={updateForm.daily_used}
                  onChange={(e) => setUpdateForm({ ...updateForm, daily_used: e.target.value })} />
              </Form>
            </>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setUpdateModalOpen(false)}>取消</Button>
          <Button positive onClick={handleUpdate}>确认更新</Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default ActionStatsTable;
